import { useState, useCallback, useMemo, useEffect } from "react";
import {
  useInviteUsersMutation,
  useSearchClientUsersMutation,
} from "@/store/api/users-api";
import { UserInviteData } from "@/models/User";
import { validateEmail } from "@/utils/validation-helpers";
import {
  useGetDashboardClientsQuery,
  useShareDashboardWithUsersMutation,
  useRevokeDashboardAccessMutation,
} from "@/store/api/dashboard-api";
import { DashboardUser } from "@/models/User";
import { useDebounce } from "@/hooks/use-debounce";
import t from "@/utils/i18n";

interface NewAccount {
  name: string;
  email: string;
  company: string;
}

export interface User extends DashboardUser {
  role: string;
  company: string;
}

type ValidationErrors = Record<
  number,
  { name?: string; email?: string; company?: string }
>;

export const useShareModalLogic = (
  dashboardId: string,
  closeAction: () => void,
  onShowSnackbar: (message: string, severity: "error" | "success") => void,
  onAccessChanged?: () => void,
) => {
  // --- STATE ---
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [activeTab, setActiveTab] = useState(0);
  const [newAccounts, setNewAccounts] = useState<NewAccount[]>([
    { name: "", email: "", company: "" },
  ]);
  const [revocationTarget, setRevocationTarget] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [shareAllPublishedDashboards, setShareAllPublishedDashboards] =
    useState(true);

  // --- API HOOKS ---
  const [inviteUsers, { isLoading: isInviting }] = useInviteUsersMutation();
  const [shareDashboardWithUsers, { isLoading: isSharing }] =
    useShareDashboardWithUsersMutation();
  const { data: clientData } = useGetDashboardClientsQuery(dashboardId, {
    skip: !dashboardId,
  });

  const [searchUsers, { isLoading: isSearchingUsers, data: searchResults }] =
    useSearchClientUsersMutation();

  const [revokeAccess, { isLoading: isRevoking }] =
    useRevokeDashboardAccessMutation();

  // Get all client IDs
  const allClientIds = useMemo(() => {
    if (!clientData) return [];

    const ids = clientData.additionalClients.map((c) => c.id);
    if (clientData.primaryClient) {
      ids.push(clientData.primaryClient.id);
    }
    return ids;
  }, [clientData]);

  // Debounce the search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (allClientIds.length === 0) return;

    const clientIdsToSearch =
      selectedClientId === "all" || !selectedClientId
        ? allClientIds
        : [selectedClientId];

    searchUsers({
      dashboardId,
      body: {
        clientIds: clientIdsToSearch,
        search: debouncedSearchTerm,
      },
    });
  }, [
    allClientIds,
    dashboardId,
    selectedClientId,
    debouncedSearchTerm,
    searchUsers,
  ]);

  // Mark as initially loaded when we have search results (not when we make the call)
  useEffect(() => {
    if (searchResults && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [searchResults, hasInitiallyLoaded]);

  const sharedUsers = useMemo(() => {
    return (searchResults?.withAccess || []).map((user) => ({
      ...user,
      role: "Viewer",
      company: user.clientName,
    }));
  }, [searchResults]);

  const nonSharedUsers: User[] = useMemo(() => {
    return (searchResults?.withoutAccess || []).map((user) => ({
      ...user,
      role: "",
      company: user.clientName,
    }));
  }, [searchResults]);

  // --- DERIVED/MEMOIZED VALUES ---
  const allExistingUsers = useMemo(
    () => [...sharedUsers, ...nonSharedUsers],
    [sharedUsers, nonSharedUsers],
  );

  const emailToUserIdMap = useMemo(() => {
    return allExistingUsers.reduce<Record<string, string>>((acc, user) => {
      acc[user.email.toLowerCase()] = user.id;
      return acc;
    }, {});
  }, [allExistingUsers]);

  const getPluralKey = useCallback((baseKey: string, count: number) => {
    if (count === 1) {
      return t(`${baseKey}_one`);
    }
    return t(`${baseKey}_many`, { count });
  }, []);

  // --- HANDLERS (for Share Existing tab) ---
  const handleSelectUser = useCallback(
    (email: string) => {
      if (revocationTarget) return;

      setSelectedUsers((prev) =>
        prev.includes(email)
          ? prev.filter((e) => e !== email)
          : [...prev, email],
      );
    },
    [revocationTarget],
  );

  const handleClientFilterChange = useCallback((clientId: string) => {
    setSelectedClientId(clientId);
  }, []);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
      setRevocationTarget(null);
    },
    [],
  );

  const handleRevokeClick = useCallback((email: string) => {
    setRevocationTarget(email);
    setSelectedUsers((prev) => prev.filter((e) => e !== email));
  }, []);

  const confirmRevoke = useCallback(
    async (email: string) => {
      const user = allExistingUsers.find(
        (u) => u.email.toLowerCase() === email.toLowerCase(),
      );
      const userId = user?.id;
      const userName = user?.name || email;

      if (!userId) {
        console.error(`User ID not found for email: ${email}`);
        setRevocationTarget(null);
        return;
      }

      try {
        await revokeAccess({
          dashboardId,
          body: { userIds: [userId] },
        }).unwrap();

        onShowSnackbar(
          t("messages.success.accessRevoked", { userName: userName }),
          "success",
        );

        onAccessChanged?.();

        const clientIdsToSearch =
          selectedClientId === "all" || !selectedClientId
            ? allClientIds
            : [selectedClientId];

        searchUsers({
          dashboardId,
          body: {
            clientIds: clientIdsToSearch,
            search: searchTerm,
          },
        });

        setRevocationTarget(null);
      } catch (error) {
        console.error(`Failed to revoke access for ${email}:`, error);
        onShowSnackbar(
          t("messages.error.failedToRevokeAccess", { userName: userName }),
          "error",
        );
        setRevocationTarget(null);
      }
    },
    [
      dashboardId,
      revokeAccess,
      searchUsers,
      allClientIds,
      allExistingUsers,
      selectedClientId,
      searchTerm,
      onShowSnackbar,
      onAccessChanged,
    ],
  );

  const cancelRevoke = useCallback(() => {
    setRevocationTarget(null);
  }, []);

  // --- HANDLERS (for Invite New Account tab) ---
  const handleAddAccount = useCallback(() => {
    setNewAccounts((prev) => [...prev, { name: "", email: "", company: "" }]);
    setValidationErrors({});
  }, []);

  const handleRemoveAccount = useCallback((index: number) => {
    setNewAccounts((prev) => prev.filter((_, i) => i !== index));

    setValidationErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (newErrors[index]) {
        delete newErrors[index];
      }

      const shiftedErrors: ValidationErrors = {};
      Object.keys(newErrors).forEach((key) => {
        const i = parseInt(key);
        if (i < index) {
          shiftedErrors[i] = newErrors[i];
        } else if (i > index) {
          shiftedErrors[i - 1] = newErrors[i];
        }
      });
      return shiftedErrors;
    });
  }, []);

  const handleAccountChange = useCallback(
    (index: number, field: keyof NewAccount, value: string) => {
      setNewAccounts((prev) =>
        prev.map((account, i) =>
          i === index ? { ...account, [field]: value } : account,
        ),
      );

      setValidationErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        if (newErrors[index]?.[field]) {
          delete newErrors[index][field];
        }
        return newErrors;
      });
    },
    [],
  );

  // --- VALIDATION & INVITE LOGIC ---

  const validateNewAccounts = useCallback(() => {
    const errors: ValidationErrors = {};
    let hasError = false;
    const seenEmails = new Set();

    newAccounts.forEach((account, index) => {
      const email = account.email.trim().toLowerCase();

      // 1. Name validation
      if (!account.name.trim()) {
        errors[index] = { ...errors[index], name: "Name is required." };
        hasError = true;
      }

      // 2. Email validation
      if (email) {
        if (!validateEmail(email)) {
          errors[index] = { ...errors[index], email: "Invalid email format." };
          hasError = true;
        }
        if (seenEmails.has(email)) {
          errors[index] = { ...errors[index], email: "Email is a duplicate." };
          hasError = true;
        } else {
          seenEmails.add(email);
        }
      } else {
        errors[index] = { ...errors[index], email: "Email is required." };
        hasError = true;
      }

      // 3. Client Company validation
      if (!account.company) {
        errors[index] = {
          ...errors[index],
          company: "Client company is required.",
        };
        hasError = true;
      }
    });

    setValidationErrors(errors);
    return !hasError;
  }, [newAccounts]);

  const handleInvite = useCallback(async () => {
    if (validateNewAccounts()) {
      const usersToInvite: UserInviteData[] = newAccounts.map((account) => ({
        clientId: account.company,
        username: account.name.trim(),
        email: account.email.trim(),
      }));

      try {
        const result = await inviteUsers({
          dashboardId,
          body: {
            users: usersToInvite,
            shareAllPublishedDashboards: shareAllPublishedDashboards,
          },
        }).unwrap();

        onShowSnackbar(
          getPluralKey("messages.success.newUsersInvited", result.invitedCount),
          "success",
        );

        onAccessChanged?.();
        closeAction();
      } catch (error: any) {
        if (error?.status === 400 && error?.data?.data) {
          const existingEmails = error.data.data as string[];
          const errors: ValidationErrors = {};

          newAccounts.forEach((account, index) => {
            const email = account.email.trim().toLowerCase();
            if (
              existingEmails.some(
                (existingEmail) => existingEmail.toLowerCase() === email,
              )
            ) {
              errors[index] = {
                ...errors[index],
                email: "This email is already existing.",
              };
            }
          });

          setValidationErrors(errors);
          onShowSnackbar(t("messages.error.failedToInviteUsers"), "error");
        } else {
          console.error("Failed to invite users:", error);
          onShowSnackbar(t("messages.error.failedToInviteUsers"), "error");
        }
      }
    } else {
      console.log("Validation failed.");
    }
  }, [
    newAccounts,
    validateNewAccounts,
    inviteUsers,
    dashboardId,
    closeAction,
    onShowSnackbar,
    getPluralKey,
    shareAllPublishedDashboards,
    onAccessChanged,
  ]);

  const handleShare = useCallback(async () => {
    const userIds = selectedUsers
      .map((email) => emailToUserIdMap[email.toLowerCase()])
      .filter((id): id is string => !!id);

    if (userIds.length === 0) {
      console.warn("No valid users selected to share. Aborting share.");
      return;
    }

    try {
      await shareDashboardWithUsers({
        dashboardId,
        body: { userIds },
      }).unwrap();

      onShowSnackbar(
        getPluralKey("messages.success.dashboardShared", userIds.length),
        "success",
      );

      onAccessChanged?.();

      closeAction();
    } catch (error) {
      console.error("Failed to share dashboard with existing users:", error);
      onShowSnackbar(t("messages.error.failedToShareDashboard"), "error");
    }
  }, [
    selectedUsers,
    emailToUserIdMap,
    shareDashboardWithUsers,
    dashboardId,
    closeAction,
    onShowSnackbar,
    getPluralKey,
    onAccessChanged,
  ]);

  return {
    // State
    selectedUsers,
    searchTerm,
    selectedClientId,
    activeTab,
    newAccounts,
    revocationTarget,
    validationErrors,
    isInviting,
    isSearchingUsers,
    isSharing,
    isRevoking,
    hasInitiallyLoaded,

    // Data/List
    sharedUsers,
    nonSharedUsers,
    allExistingUsers,

    // Handlers
    setSearchTerm,
    handleSelectUser,
    handleClientFilterChange,
    handleTabChange,
    handleRevokeClick,
    confirmRevoke,
    cancelRevoke,
    handleAddAccount,
    handleRemoveAccount,
    handleAccountChange,
    handleInvite,
    handleShare,
    shareAllPublishedDashboards,
    setShareAllPublishedDashboards,
  };
};
